import React from 'react';

// Adjust all clothing artwork together for the selected head.
// The neckline moves down while the bottom remains at y: 400.
export default function AvatarClothing({
  characterType = 'human_male',
  clothing,
  skin,
  layout,
}) {
  const offsetY = layout?.clothingOffsetY ?? 0;

  const necklineY = 294;
  const bottomY = 400;
  const originalHeight = bottomY - necklineY;
  const scaleY = (originalHeight - offsetY) / originalHeight;

  return (
    <g
      transform={
        offsetY === 0
          ? undefined
          : `translate(0 ${necklineY + offsetY}) scale(1 ${scaleY}) translate(0 ${-necklineY})`
      }
    >
      <ClothingArtwork
        characterType={characterType}
        clothing={clothing}
        skin={skin}
      />
    </g>
  );
}

// Existing clothing designs remain together in their original coordinates.
function ClothingArtwork({
  characterType = 'human_male',
  clothing,
  skin,
}) {

  const isFemale = characterType === 'human_female';

  const color = clothing?.color || '#7060E8';
  const shadow = clothing?.shadow || '#5143B8';
  const highlight = clothing?.highlight || '#9486F5';
  const styleId = clothing?.id || 'tshirt_01';

  const silhouette = isFemale
    ? `
      M174 294
      Q153 295 137 308
      Q118 323 110 348
      L99 382
      Q97 388 104 391
      L133 400
      L267 400
      L296 391
      Q303 388 301 382
      L290 348
      Q282 323 263 308
      Q247 295 226 294
      Q200 324 174 294
      Z
    `
    : `
      M174 294
      Q150 294 129 307
      Q106 321 96 347
      L83 382
      Q81 388 88 391
      L120 400
      L280 400
      L312 391
      Q319 388 317 382
      L304 347
      Q294 321 271 307
      Q250 294 226 294
      Q200 324 174 294
      Z
    `;

  const leftSeam = isFemale
    ? 'M143 340 Q136 368 137 397'
    : 'M132 338 Q124 365 124 397';

  const rightSeam = isFemale
    ? 'M257 340 Q264 368 263 397'
    : 'M268 338 Q276 365 276 397';

  const seams = (
    <g
      fill="none"
      stroke={shadow}
      strokeWidth="7"
      strokeLinecap="round"
    >
      <path d={leftSeam} />
      <path d={rightSeam} />
    </g>
  );

  switch (styleId) {
    case 'hoodie_01':
      return (
        <g>
          {/* Folded hood frames the base of the neck. */}
          <path
            d="
              M174 281
              Q144 279 143 309
              Q145 333 174 337
              L226 337
              Q255 333 257 309
              Q256 279 226 281
              L226 300
              Q200 323 174 300
              Z
            "
            fill={shadow}
          />

          <path d={silhouette} fill={color} />
          {seams}

          <path
            d="M165 296 Q200 337 235 296"
            fill="none"
            stroke={shadow}
            strokeWidth="13"
            strokeLinecap="round"
          />

          <g
            fill="none"
            stroke={highlight}
            strokeWidth="5"
            strokeLinecap="round"
          >
            <path d="M184 320 L181 349" />
            <path d="M216 320 L219 349" />
          </g>

          <path
            d="M166 373 L158 399 H242 L234 373 Z"
            fill={shadow}
          />

          <path
            d="M175 375 H225"
            fill="none"
            stroke={highlight}
            strokeWidth="4"
            strokeLinecap="round"
          />
        </g>
      );

    case 'shirt_01':
      return (
        <g>
          <path d={silhouette} fill={color} />
          {seams}

          {/* Button placket. */}
          <path
            d="M200 320 V400"
            fill="none"
            stroke={shadow}
            strokeWidth="5"
          />

          {/* Two collar leaves frame a small V opening. */}
          <path
            d="M174 291 L200 317 L182 336 L163 307 Z"
            fill={highlight}
          />

          <path
            d="M226 291 L200 317 L218 336 L237 307 Z"
            fill={highlight}
          />

          <g fill={highlight}>
            <circle cx="200" cy="344" r="3.5" />
            <circle cx="200" cy="366" r="3.5" />
            <circle cx="200" cy="388" r="3.5" />
          </g>

          <path
            d="M228 347 H250 V367 Q239 378 228 367 Z"
            fill={shadow}
          />
        </g>
      );

    case 'jacket_01':
      return (
        <g>
          <path d={silhouette} fill={color} />
          {seams}

          {/* Neutral undershirt stays visible inside the jacket. */}
          <path
            d="
              M176 296
              Q200 320 224 296
              L230 400
              H170
              Z
            "
            fill="#FFFCF6"
          />

          {/* Open jacket edges and lapels. */}
          <path
            d="
              M174 294
              L163 316
              L179 331
              L168 343
              L189 400
              L187 330
              Z
            "
            fill={shadow}
          />

          <path
            d="
              M226 294
              L237 316
              L221 331
              L232 343
              L211 400
              L213 330
              Z
            "
            fill={shadow}
          />

          <g
            fill="none"
            stroke={highlight}
            strokeWidth="5"
            strokeLinecap="round"
          >
            <path d="M143 365 L158 362" />
            <path d="M242 362 L257 365" />
          </g>
        </g>
      );

    case 'sleeveless_01':
      return (
        <g>
          {/* Exposed shoulders and arms match the selected skin. */}
          <path
            d={silhouette}
            fill={skin?.color || 'none'}
          />

          <path
            d="
              M160 299
              L175 294
              Q200 324 225 294
              L240 299
              Q231 332 247 353
              L253 400
              H147
              L153 353
              Q169 332 160 299
              Z
            "
            fill={color}
          />

          <g
            fill="none"
            stroke={shadow}
            strokeWidth="7"
            strokeLinecap="round"
          >
            <path d="M175 297 Q200 324 225 297" />
            <path d="M160 302 Q169 332 155 351" />
            <path d="M240 302 Q231 332 245 351" />
          </g>

          <path
            d="M180 343 H220"
            fill="none"
            stroke={highlight}
            strokeWidth="5"
            strokeLinecap="round"
          />
        </g>
      );

    case 'tshirt_01':
    default:
      return (
        <g>
          <path d={silhouette} fill={color} />
          {seams}

          <path
            d="M174 296 Q200 325 226 296"
            fill="none"
            stroke={shadow}
            strokeWidth="12"
            strokeLinecap="round"
          />

          <g
            fill="none"
            stroke={highlight}
            strokeWidth="7"
            strokeLinecap="round"
          >
            <path
              d={
                isFemale
                  ? 'M149 321 Q156 317 163 317'
                  : 'M142 320 Q152 315 162 315'
              }
            />

            <path
              d={
                isFemale
                  ? 'M237 317 Q244 317 251 321'
                  : 'M238 315 Q248 315 258 320'
              }
            />
          </g>
        </g>
      );
  }
}
